import React, { useState, useMemo } from 'react';
import { DeliveryLog } from '../../types/domain';
import { DataTableColumn, FilterRule } from '../../types/grid';
import { DataTable } from '../grid/DataTable';
import { IdCell } from '../grid/IdCell';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import {
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Clock,
  HelpCircle,
  Copy,
  Check,
} from 'lucide-react';

import { SortingState } from '@tanstack/react-table';

interface DeliveryLogsGridProps {
  logs: DeliveryLog[];
  totalLogs: number;
  page: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newSize: number) => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
  canEdit: boolean;
  onRetry: (logId: string) => Promise<void>;
  retryingLogId: string | null;
  onServerSortChange?: (field: string, dir: 'asc' | 'desc' | '', allSorts?: SortingState) => void;
  onServerFilterChange?: (rules: FilterRule[]) => void;
  filterRules?: FilterRule[];
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  sortRules?: Array<{ id: string; desc: boolean }>;
  persistenceKey?: string;
}

export const DeliveryLogsGrid: React.FC<DeliveryLogsGridProps> = ({
  logs,
  totalLogs,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  isRefreshing = false,
  isLoading = false,
  canEdit,
  onRetry,
  retryingLogId,
  onServerSortChange,
  onServerFilterChange,
  filterRules,
  sortBy,
  sortDir,
  sortRules,
  persistenceKey,
}) => {
  const { tables } = useAuth();
  const [selectedErrorLog, setSelectedErrorLog] = useState<DeliveryLog | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyError = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getDatastoreName = (row: DeliveryLog): string => {
    if (row.table_name && row.table_name.trim() && row.table_name !== 'Datastore' && row.table_name !== 'Default Datastore') {
      return row.table_name;
    }
    if (row.table_slug && row.table_slug.trim() && row.table_slug !== 'datastore') {
      return row.table_slug;
    }
    if ((row as any).table_id && tables && tables.length > 0) {
      const matched = tables.find((t) => t.id === (row as any).table_id);
      if (matched) return matched.name;
    }
    if (tables && tables.length > 0) {
      return tables[0].name;
    }
    return '—';
  };

  const columns = useMemo<DataTableColumn<DeliveryLog>[]>(
    () => [
      {
        id: 'submission_id',
        header: 'Submission ID',
        accessorFn: (row) => row.submission_id,
        width: 140,
        minWidth: 130,
        maxWidth: 170,
        sortable: true,
        filterable: true,
        pinned: 'left',
        isSystem: true,
        cell: ({ row }) => (
          <div className="flex items-center">
            <IdCell id={row.submission_id} />
          </div>
        ),
      },
      {
        id: 'data_store',
        header: 'Data Store',
        accessorFn: (row) => getDatastoreName(row),
        width: 160,
        minWidth: 130,
        maxWidth: 220,
        sortable: true,
        filterable: true,
        cell: ({ row, value }) => {
          const name = getDatastoreName(row) || (typeof value === 'string' && value !== '—' ? value : '—');
          return (
            <span className="text-xs font-sans text-cf-textPrimary truncate block whitespace-nowrap" title={name}>
              {name}
            </span>
          );
        },
      },
      {
        id: 'rule_name',
        header: 'Rule Name',
        accessorFn: (row) => row.rule_name || row.rule_id || 'System Notification',
        width: 180,
        minWidth: 120,
        maxWidth: 240,
        sortable: true,
        filterable: true,
        cell: ({ row, value }) => (
          <span
            className="font-sans font-medium text-cf-textPrimary truncate block whitespace-nowrap"
            title={row.rule_name || ''}
          >
            {value}
          </span>
        ),
      },
      {
        id: 'recipient',
        header: 'Recipient',
        accessorKey: 'recipient',
        width: 180,
        minWidth: 140,
        maxWidth: 240,
        dataType: 'email',
        sortable: true,
        filterable: true,
      },
      {
        id: 'created_at',
        header: 'Timestamp',
        accessorKey: 'created_at',
        width: 190,
        minWidth: 170,
        maxWidth: 220,
        dataType: 'date',
        truncate: false,
        sortable: true,
        filterable: true,
      },
      {
        id: 'duration_ms',
        header: 'Latency',
        accessorFn: (row) => row.duration_ms,
        width: 100,
        minWidth: 80,
        maxWidth: 120,
        dataType: 'number',
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <span className="text-cf-textMuted tabular-nums text-xs whitespace-nowrap">
            {typeof row.duration_ms === 'number' && row.duration_ms > 0
              ? `${Number(row.duration_ms).toFixed(2)} ms`
              : '—'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        width: 140,
        minWidth: 120,
        maxWidth: 160,
        dataType: 'status',
        options: [
          { value: 'sent', label: 'Sent / Delivered', color: 'emerald' },
          { value: 'failed', label: 'Failed', color: 'rose' },
          { value: 'retrying', label: 'Retrying', color: 'amber' },
        ],
        sortable: true,
        filterable: true,
        cell: ({ row }) => {
          const isDelivered = row.status === 'delivered' || row.status === 'sent';
          const isFailed = row.status === 'failed';
          const isRetrying = row.status === 'retrying';
          return (
            <div className="flex items-center gap-1.5 whitespace-nowrap">
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  isDelivered
                    ? 'bg-emerald-500 dark:bg-emerald-400'
                    : isFailed
                    ? 'bg-rose-500 dark:bg-rose-400'
                    : 'bg-amber-500 dark:bg-amber-400'
                }`}
              />
              <span className="text-xs font-medium text-cf-textPrimary">
                {isDelivered ? 'Delivered' : isFailed ? 'Failed' : isRetrying ? 'Retrying' : row.status}
              </span>

              {isFailed && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedErrorLog(row);
                  }}
                  className="hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 ml-0.5"
                  title="View error details"
                >
                  <HelpCircle size={13} />
                </Button>
              )}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        width: 100,
        minWidth: 96,
        maxWidth: 120,
        pinned: 'right',
        isSystem: true,
        sortable: false,
        filterable: false,
        hideable: false,
        cell: ({ row }) => {
          if (row.status !== 'failed' || !canEdit) {
            return null;
          }
          const isThisRetrying = retryingLogId === row.id;
          return (
            <div className="flex items-center justify-end px-1">
              <Button
                variant="ghost"
                size="icon-xs"
                disabled={isThisRetrying}
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry(row.id);
                }}
                title="Retry failed notification"
              >
                <RotateCcw size={14} className={isThisRetrying ? 'animate-spin text-cf-orange' : ''} />
              </Button>
            </div>
          );
        },
      },
    ],
    [tables, canEdit, retryingLogId, onRetry]
  );

  return (
    <div className="flex flex-col h-full w-full min-h-0 min-w-0">
      <DataTable<DeliveryLog>
        data={logs}
        columns={columns}
        serverSide={true}
        totalRows={totalLogs}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        isLoading={isLoading}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        onServerSortChange={onServerSortChange}
        onServerFilterChange={onServerFilterChange}
        initialFilterRules={filterRules}
        initialSorting={sortRules && sortRules.length > 0 ? sortRules : undefined}
        initialSortField={sortBy || 'created_at'}
        initialSortDir={sortDir || 'desc'}
        tableName="delivery-logs"
        persistenceKey={persistenceKey || 'delivery_logs'}
        emptyTitle="No delivery log records found"
        emptySubtitle="Try adjusting your active filters or search."
      />

      {selectedErrorLog && (
        <Modal
          isOpen={true}
          onClose={() => {
            setSelectedErrorLog(null);
            setCopied(false);
          }}
          title="Delivery Error Details"
          maxWidth="md"
        >
          <div className="space-y-4 text-xs font-sans">
            {/* Status overview */}
            <div className="flex items-center justify-between p-3 rounded bg-cf-bg border border-cf-border">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-rose-500 flex-shrink-0" />
                <span className="font-semibold text-xs text-cf-textPrimary">Delivery Failed</span>
                <span className="text-xs text-cf-textMuted">
                  (Attempt {selectedErrorLog.attempt_number || selectedErrorLog.retry_count || 1} of 5)
                </span>
              </div>
              {selectedErrorLog.status_code ? (
                <span className="text-xs px-2 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20 font-semibold">
                  {selectedErrorLog.provider_type === 'smtp' ? 'SMTP' : 'HTTP'} {selectedErrorLog.status_code}
                </span>
              ) : null}
            </div>

            {/* Error message block */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-cf-textMuted uppercase tracking-wider">
                  Error Details
                </span>
                {selectedErrorLog.error_message && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => handleCopyError(selectedErrorLog.error_message || '')}
                    leftIcon={copied ? <Check size={11} className="text-emerald-500 dark:text-emerald-400" /> : <Copy size={11} />}
                    className="h-auto p-0 text-cf-textMuted hover:text-cf-textPrimary"
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </div>
              <div className="p-3 rounded bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 text-xs break-all select-text leading-relaxed font-mono">
                {selectedErrorLog.error_message || 'An unspecified error occurred during notification dispatch.'}
              </div>
            </div>

            {/* Key-Value Details */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="p-2.5 rounded bg-cf-bg border border-cf-border">
                <div className="text-xs text-cf-textMuted uppercase tracking-wider mb-1">
                  Recipient
                </div>
                <div className="text-xs text-cf-textPrimary truncate select-text" title={selectedErrorLog.recipient}>
                  {selectedErrorLog.recipient || '—'}
                </div>
              </div>

              <div className="p-2.5 rounded bg-cf-bg border border-cf-border">
                <div className="text-xs text-cf-textMuted uppercase tracking-wider mb-1">
                  Rule Name
                </div>
                <div className="font-sans text-xs text-cf-textPrimary truncate" title={selectedErrorLog.rule_name || ''}>
                  {selectedErrorLog.rule_name || 'System Notification'}
                </div>
              </div>

              <div className="p-2.5 rounded bg-cf-bg border border-cf-border">
                <div className="text-xs text-cf-textMuted uppercase tracking-wider mb-1">
                  Data Store
                </div>
                <div className="font-sans text-xs text-cf-textPrimary truncate">
                  {selectedErrorLog.table_name || selectedErrorLog.table_slug || 'Default Datastore'}
                </div>
              </div>

              <div className="p-2.5 rounded bg-cf-bg border border-cf-border">
                <div className="text-xs text-cf-textMuted uppercase tracking-wider mb-1">
                  Latency
                </div>
                <div className="text-xs text-cf-textPrimary">
                  {typeof selectedErrorLog.duration_ms === 'number' && selectedErrorLog.duration_ms > 0
                    ? `${Number(selectedErrorLog.duration_ms).toFixed(2)} ms`
                    : '—'}
                </div>
              </div>
            </div>

            {/* Response body payload if present */}
            {selectedErrorLog.response_body && (
              <div>
                <div className="text-xs text-cf-textMuted uppercase tracking-wider mb-1.5 flex items-center justify-between">
                  <span>Server Response Body</span>
                  <span className="text-xs text-zinc-500">RAW PAYLOAD</span>
                </div>
                <pre className="p-3 rounded bg-cf-bg border border-cf-border text-cf-textSecondary font-mono text-xs max-h-36 overflow-y-auto whitespace-pre-wrap break-all select-text">
                  {selectedErrorLog.response_body}
                </pre>
              </div>
            )}

            {/* Footer Buttons */}
            <div className="flex items-center justify-between pt-3 border-t border-cf-border">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSelectedErrorLog(null);
                  setCopied(false);
                }}
              >
                Dismiss
              </Button>

              {canEdit && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    const id = selectedErrorLog.id;
                    setSelectedErrorLog(null);
                    setCopied(false);
                    onRetry(id);
                  }}
                  disabled={retryingLogId === selectedErrorLog.id}
                  isLoading={retryingLogId === selectedErrorLog.id}
                  leftIcon={retryingLogId !== selectedErrorLog.id ? <RotateCcw size={12} /> : undefined}
                >
                  {retryingLogId === selectedErrorLog.id ? 'Retrying...' : 'Retry Delivery Now'}
                </Button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
